/* eslint-disable react/no-did-mount-set-state */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Button, FormControl } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import Icon from '../utils/Icon';
import { localizedNotifyError } from '../utils/notify';
import { getSectionCommentingErrorMessage } from '../utils/section';
// eslint-disable-next-line import/no-unresolved
import urls from '@city-assets/urls.json';
// eslint-disable-next-line import/no-unresolved
import localization from '@city-i18n/localization.json';
import leafletMarkerIconUrl from '../../assets/images/leaflet/marker-icon.png';
import Leaflet from 'leaflet';
import leafletMarkerShadowUrl from '../../assets/images/leaflet/marker-shadow.png';
import leafletMarkerRetinaIconUrl from '../../assets/images/leaflet/marker-icon-2x.png';
import Sticker from './Hearing/sortable-comment-list/comment-list/comment/Sticker';
import getMessage from '../utils/getMessage';
import PinUnpinButton from './PinUnpinButton';
import CommentFormToolsPopup from './CommentFormToolsPopup';
import classNames from 'classnames';
import { getUser } from '../store/selectors/user';
import { getFormState } from '../store/selectors/commentEditorSelectors';
import { initCommentForm } from '../store/actions/commentEditorActions';
import DigiraatiAutoResizeTextarea from './forms/AutoResizeTextarea';


Leaflet.Marker.prototype.options.icon = new Leaflet.Icon({
  iconUrl: leafletMarkerIconUrl,
  shadowUrl: leafletMarkerShadowUrl,
  iconRetinaUrl: leafletMarkerRetinaIconUrl,
  iconSize: [25, 41],
  iconAnchor: [13, 41],
});

const BaseCommentForm = ({
  defaultNickname = '',
  editedText,
  cancelEditing,
  collapseForm,
  onOverrideCollapse = () => {},
  hasDelayToSaveType = false,
  setEditing,
  nicknamePlaceholder,
  hideNameForm,
  canComment, 
  section,
  isReply = false, 
  allowFiles = true,
  onPostComment,
  commentMaxLength = 3000,
  canHavePinUnpinButton = true,
}) =>  {
  const [id, setId] = useState( Math.random().toString(36).slice(2, 7) );
  const [storedTimeout, setStoredTimeout] = useState(null)

  const [collapsed, setCollapsed] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [nickname, setNickname] = useState(defaultNickname || '');
  const [imageTooBig, setImageTooBig] = useState(false);
  const [images, setImages] = useState([]);
  const [pinned, setPinned] = useState(false);
  const [showAlert, setShowAlert] = useState(true);
  const [hideName, setHideName] = useState(false);
  const [sticker, setSticker] = useState(null);

  const [formToolsOpen, setFormToolsOpen] = useState(null)

  // The shady ones
  const [geojson, setGeojson] = useState({});
  const [mapCommentText, setMapCommentText] = useState('')

  const commentTextFieldRef = useRef();

  const user = useSelector(state => getUser(state));

  const formState = useSelector(state => getFormState(state, id));
  const dispatch = useDispatch();

  const loading = formState?.loading;

  useEffect(() => {
    dispatch(initCommentForm(id))
    if (isUserAdmin()) {
      setNickname(defaultNickname);
    }
    if (!!editedText && editedText !== commentText) {
      setCommentText(editedText);
    }
  }, [])

  useEffect(() => {
    if (formState?.success) {
      // Sending success -> clear and close the form
      clearState();
      toggle();
    }
  }, [formState])

  useEffect(() => {
    if (collapseForm) {
      clearCommentText();
      toggle();
    }
  }, [collapseForm])

  const clearState = () => {
    setCollapsed(!collapsed);
    setCommentText('');
    setNickname(defaultNickname || '');
    setImageTooBig(false);
    setImages([]);
    setPinned(false);
    setShowAlert(true);
    setHideName(false);
    setMapCommentText('');
    setSticker(null);
    setFormToolsOpen(null)
  }

  const toggle = () => {
    if (canComment) {
      cancelEditing();
      clearState();
      if (onOverrideCollapse instanceof Function) {
        onOverrideCollapse();
      }
    } else {
      localizedNotifyError(getSectionCommentingErrorMessage(section));
    }
  }

  const handleTextChange = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setCommentText(event.target.value);
    if (hasDelayToSaveType) {
      const COMMENT_POLL_INTERVAL_IN_MS = 1000;
      clearTimeout(storedTimeout);
      const time = setTimeout(() => {
        if (commentText !== editedText) {
          setEditing(commentText);
        }
      }, COMMENT_POLL_INTERVAL_IN_MS);
      setStoredTimeout(time);
    } else {
      if (event.target.value !== editedText) {
        setEditing(event.target.value);
      }
    }
  }

  /**
   * @deprecated
   */
  const handleNicknameChange = (event) => {
    setNickname(event.target.value);
  }

  const clearCommentText = () => {
    setCommentText('');
  }

  const submitComment = () => {
    let _pluginData = undefined; // getPluginData();
    let _nickname =
      nickname === ''
        ? nicknamePlaceholder
        : nickname;
    let _commentText =
      commentText === null ? '' : commentText;
    let _geojson = geojson;
    let _label = null;
    let _images = images;
    let _pinned = pinned;
    let _mapCommentText = mapCommentText;
    let _sticker = sticker;
    onPostComment(
      id,
      _commentText,
      _nickname,
      _pluginData,
      _geojson,
      _label,
      _images,
      _pinned,
      _mapCommentText,
      _sticker,
    );
  }


  /**
   * Determines whether the logged in user is admin or not.
   * The array in users with key adminOrganizations should be of length > 0
   */
  const isUserAdmin = () =>
    user &&
    Array.isArray(user.adminOrganizations) &&
    user.adminOrganizations.length > 0;


  /**
   * When user type is admin, an alert is shown, use this method to close the alert.
   */
  const handleCloseAlert = () => {
    setShowAlert(false);
  };

  /**
   * When logged in as admin, user may chose to hide their identity.
   */
  const handleToggleHideName = () => {
    setNickname(!hideName ? getMessage('employee') : defaultNickname);
    setHideName(!hideName);
  };

  /**
   * Toggle the pinning of comment
   */
  const handleTogglePin = () => {
    setPinned(!pinned);
  };


  const getId = (prefix) => {
    return prefix + '-' + id;
  }

  const setCommentSticker = (stickerId) => {
    setSticker(stickerId);
  }

  const setOrToggleFormTools = (tools) => {
    if (formToolsOpen === tools) {
      setFormToolsOpen(null); // toggle off
    } else {
      setFormToolsOpen(tools); // open
    }
  }

  return (
    <div className="comment-form">
      <form disabled={loading}>
        <div className="comment-form__heading-container">
          {!!formToolsOpen && 
            <CommentFormToolsPopup
              formToolsOpen={formToolsOpen} 
              setFormToolsOpen={setFormToolsOpen}
              setCommentSticker={setCommentSticker}
              sticker={sticker}
              images={images}
              setImages={setImages}
              imageTooBig={imageTooBig}
              setImageTooBig={setImageTooBig}
              getId={getId}
              commentTextFieldRef={commentTextFieldRef}
              isUserAdmin={isUserAdmin}
              user={user}
              hideNameForm={hideNameForm}
              commentText={commentText}
              setCommentText={setCommentText}
              showAlert={showAlert}
              handleCloseAlert={handleCloseAlert}
              hideName={hideName}
              handleToggleHideName={handleToggleHideName}
              nickname={nickname}
            />
          }
        </div>
        {
          sticker &&
          <div>
            <Sticker stickerId={sticker} />
            <Button onClick={() => setCommentSticker(null)}>
              <FormattedMessage id="delete" />
            </Button>
          </div>
        }
        <div>
          {/*<label htmlFor={getId('commentTextField')}>
              <FormattedMessage id={isReply ? "writeReply" : "writeBaseComment"} />
            </label>*/}
          <div className='textarea-container'>
            <DigiraatiAutoResizeTextarea
              ref={commentTextFieldRef}
              autoFocus
              value={commentText}
              onChange={handleTextChange}
              id={getId('commentTextField')}
              disabled={loading}
              placeholder={getMessage("baseCommentFormPlaceholder")}
              maxLength={commentMaxLength}
              ariaLabelText={isReply ? "writeReply" : "writeBaseComment"}
            />
          </div>
          <div className="buttons-container">
            <div className="left-buttons">
              <button type='button' 
                disabled={loading}
                className={classNames(
                  'comment-form-button',
                  {
                    'digi-button__selected': formToolsOpen === 'emoji'
                  }
                )}
                onClick={() => setOrToggleFormTools('emoji')}
                aria-label={getMessage('emojis')}
                title={getMessage('emojis')}
                >😀</button>
              {allowFiles && 
              <button type='button' 
                disabled={loading}
                className={classNames(
                  'comment-form-button',
                  {
                    'digi-button__selected': formToolsOpen === 'files'
                  }
                )}
                onClick={() => setOrToggleFormTools('files')}
                style={{position: "relative"}}
                aria-label={getMessage('attachments')}
                title={getMessage('attachments')}
                ><Icon name="image" />
                { (images.length > 0) && <div style={{position: "absolute", bottom:0, right: 0}}>{images.length}</div> }
              </button>}
              {isUserAdmin() && 
                <button type='button' 
                  disabled={loading}
                  className={classNames(
                    'comment-form-button',
                    {
                      'digi-button__selected': formToolsOpen === 'nameOrg'
                    }
                  )}
                  onClick={() => setOrToggleFormTools('nameOrg')}
                  aria-label={getMessage('nameAndOrganization')}
                  title={getMessage('nameAndOrganization')}
                ><Icon name="bar-chart" />
              </button> }
              {canHavePinUnpinButton && isUserAdmin() && !isReply && (
                <div className="comment-form-button flex-center small-padding--horizontal">
                  <PinUnpinButton isPinned={pinned} handleTogglePin={handleTogglePin} formId={id}/>
                </div>
              )}
            </div>
            <div className="right-buttons">
              
              <button type='button' disabled={loading} 
                className='digi-button digi-button--text-posh digi-button--rounded digi-button--small-text' 
                onClick={toggle}
              >
                <FormattedMessage id="decline" />
              </button>
              <button type='button' 
                className='digi-button digi-button--black digi-button--black__primary-hover digi-button--rounded digi-button--small-text'
                disabled={!commentText || imageTooBig || loading}
                onClick={submitComment}
                title={getMessage('submit')}
              >
              { loading ? <Icon name="hourglass-o" spin/> : <FormattedMessage id='submit' /> }
              </button>
            </div>
          </div>
          
        </div>
      </form>
    </div>
  );
  
}

BaseCommentForm.propTypes = {
  canComment: PropTypes.bool,
  onPostComment: PropTypes.func,
  onOverrideCollapse: PropTypes.func,
  intl: PropTypes.object,
  collapseForm: PropTypes.bool,
  defaultNickname: PropTypes.string,
  nicknamePlaceholder: PropTypes.string,
  section: PropTypes.object,
  isReply: PropTypes.bool,
  allowFiles: PropTypes.bool,
  hideNameForm: PropTypes.bool,
  setEditing: PropTypes.func,
  cancelEditing: PropTypes.func,
  editedText: PropTypes.string,
  hasDelayToSaveType: PropTypes.bool,
};

const WrappedBaseCommentForm = injectIntl(BaseCommentForm);
export default WrappedBaseCommentForm;
